import React from 'react';
import {ProfileSocialLink} from '../constants';
import styles from '../Profile.module.css';

interface ProfileSocialLinksSectionProps {
  links: ProfileSocialLink[];
  isEditable?: boolean;
  loadingProvider: ProfileSocialLink['provider'] | null;
  onPrimaryAction?: (provider: ProfileSocialLink['provider']) => void;
  onDisconnect?: (provider: ProfileSocialLink['provider']) => void;
}

const variantClassNames: Record<ProfileSocialLink['variant'], string> = {
  facebook: styles.socialLinkFacebook,
  instagram: styles.socialLinkInstagram,
  twitter: styles.socialLinkTwitter,
  linkedin: styles.socialLinkLinkedin,
  youtube: styles.socialLinkYoutube,
  tiktok: styles.socialLinkTiktok,
  website: styles.socialLinkWebsite,
};

const statusClassNames: Record<ProfileSocialLink['status'], string> = {
  connected: styles.socialLinkStatusConnected,
  disconnected: styles.socialLinkStatusDisconnected,
  needs_public_url: styles.socialLinkStatusPending,
  failed: styles.socialLinkStatusFailed,
};

const socialProfileIconStyle = {
  '--profile-information-icon-url': `url("${process.env.PUBLIC_URL}/icons/social-profile.svg")`,
} as React.CSSProperties;

const socialLogoFileNames: Partial<Record<ProfileSocialLink['provider'], string>> = {
  facebook: 'logos-facebook.svg',
  instagram: 'logos-instagram.svg',
  twitter: 'logos-twitter.svg',
  linkedin: 'logos-linkedin.svg',
  youtube: 'logos-youtube.svg',
  tiktok: 'logos-tiktok.svg',
  website: 'worldwide-web-sync.svg',
};

const SocialLinkCardIcon: React.FC<{link: ProfileSocialLink}> = ({link}) => {
  const [logoLoadFailed, setLogoLoadFailed] = React.useState(false);
  const logoFileName = socialLogoFileNames[link.provider];

  if (logoFileName && !logoLoadFailed) {
    return (
      <span className={styles.socialLinkLogoFrame} aria-hidden="true">
        <img
          className={styles.socialLinkLogo}
          src={`${process.env.PUBLIC_URL}/icons/${logoFileName}`}
          alt=""
          onError={() => setLogoLoadFailed(true)}
        />
      </span>
    );
  }

  return <span className={styles.socialLinkIcon} aria-hidden="true">{link.icon}</span>;
};

const ProfileSocialLinksSection: React.FC<ProfileSocialLinksSectionProps> = ({
  links,
  isEditable = true,
  loadingProvider,
  onPrimaryAction,
  onDisconnect,
}) => (
  <section
    className={`${styles.profileSection} ${styles.profileInformationSection}`}
    aria-labelledby="social-profiles-title"
  >
    <div className={styles.profileInformationHeader}>
      <div className={styles.profileInformationHeading}>
        <div className={styles.profileInformationTitleRow}>
          <span className={styles.profileInformationHeadingIconTile} aria-hidden="true">
            <span
              className={styles.profileInformationHeadingIcon}
              style={socialProfileIconStyle}
            />
          </span>
          <h3
            id="social-profiles-title"
            className={`${styles.sectionTitle} ${styles.profileInformationTitle}`}
          >
            Social Profiles
          </h3>
        </div>
        <p className={styles.profileInformationSubtitle}>
          Connect and manage your social accounts and public links.
        </p>
      </div>
    </div>

    <div className={styles.socialProfilesDivider} aria-hidden="true" />

    {links.length > 0 ? (
      <div className={styles.socialLinksGrid}>
        {links.map((link) => {
          const isLoading = loadingProvider === link.provider;
          const cardTitleId = `social-profile-${link.provider}-title`;
          const helperText = link.helperText || (
            link.href ? 'Connected profile' : 'Not connected yet.'
          );
          const isConnectedProfile = link.status === 'connected' && Boolean(link.href);

          return (
            <article
              key={link.provider}
              className={`${styles.socialLinkCard} ${variantClassNames[link.variant]}`}
              aria-labelledby={cardTitleId}
              aria-busy={isLoading}
            >
              <div className={styles.socialLinkCardHeader}>
                <div className={styles.socialLinkCardIdentity}>
                  <SocialLinkCardIcon link={link} />
                  <div className={styles.socialLinkIdentityCopy}>
                    <h4 id={cardTitleId} className={styles.socialLinkLabel}>{link.label}</h4>
                    <span className={`${styles.socialLinkStatus} ${statusClassNames[link.status]}`}>
                      {link.statusLabel}
                    </span>
                  </div>
                </div>
              </div>

              <p
                className={`${styles.socialLinkHelperText} ${
                  isConnectedProfile ? styles.socialLinkHelperTextConnected : ''
                }`}
              >
                {helperText}
              </p>

              {isEditable || link.href ? (
                <div className={styles.socialLinkActions} aria-label={`${link.label} actions`}>
                  {isEditable ? (
                    <>
                      <button
                        type="button"
                        className={styles.socialPrimaryAction}
                        onClick={() => onPrimaryAction?.(link.provider)}
                        disabled={isLoading || !onPrimaryAction}
                        aria-label={`${link.primaryActionLabel} ${link.label}`}
                      >
                        {isLoading ? 'Working…' : link.primaryActionLabel}
                      </button>
                      {link.canDisconnect ? (
                        <button
                          type="button"
                          className={styles.socialSecondaryAction}
                          onClick={() => onDisconnect?.(link.provider)}
                          disabled={isLoading || !onDisconnect}
                          aria-label={`Disconnect ${link.label}`}
                        >
                          Disconnect
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${styles.socialPrimaryAction} ${styles.socialPrimaryActionLink}`}
                    >
                      {link.primaryActionLabel}
                    </a>
                  )}
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    ) : (
      <p className={styles.sectionEmptyText}>No public social links available yet.</p>
    )}
  </section>
);

export default React.memo(ProfileSocialLinksSection);
